import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CHIP_WRAP_SX } from '../layoutSx';

const SIZE_COLS = { small: 3, medium: 6, large: 8, full: 12 };

const Row = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: 'text.disabled', width: { xs: 72, sm: 96 }, flexShrink: 0, overflowWrap: 'anywhere' }}>{label}</Typography>
    <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{children}</Box>
  </Box>
);

/**
 * Body of the DASHBOARD_PLAN checkpoint (stageOutputJson = DashboardPlanDto, contracts §6.1):
 * objective, scope, global filters and the planned widget list. Read-only; the
 * approve / answer actions stay in HitlReviewPanel.
 */
export default function DashboardPlanReview({ plan }) {
  const { t } = useTranslation();
  if (!plan) return null;
  const controls = Array.isArray(plan.global_controls) ? plan.global_controls : [];
  const widgets = Array.isArray(plan.widgets) ? plan.widgets : [];
  const vizLabel = (v) => (v ? t(`Dashboards.vizTypes.${v}`, { defaultValue: v }) : null);

  return (
    // minWidth 0 + overflowWrap 'anywhere' so long metric / filter / column names wrap
    // inside their card instead of widening the grid past the panel.
    <Box sx={{ mb: 2, minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>{t('Dashboards.hitl.planIntro')}</Typography>
      <Stack spacing={0.75} sx={{ mb: 2 }}>
        {plan.objective && (
          <Row label={t('Dashboards.hitl.planObjective')}><Typography variant="body2">{plan.objective}</Typography></Row>
        )}
        {plan.global_scope && (
          <Row label={t('Dashboards.hitl.planScope')}><Typography variant="body2">{plan.global_scope}</Typography></Row>
        )}
        {controls.length > 0 && (
          <Row label={t('Dashboards.hitl.planControls')}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {controls.map((c) => (
                <Chip key={c.control_id} size="small" variant="outlined" sx={CHIP_WRAP_SX}
                  label={`${t(`Dashboards.controls.names.${c.control_id}`, { defaultValue: c.label || c.control_id })} · ${c.type}`} />
              ))}
            </Box>
          </Row>
        )}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{t('Dashboards.hitl.planWidgets')}</Typography>
        <Chip label={widgets.length} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 1, minWidth: 0 }}>
        {widgets.map((w, i) => {
          const size = (w.layout_hint && w.layout_hint.size) || 'medium';
          const span = SIZE_COLS[size] || 6;
          const metrics = Array.isArray(w.required_metrics) ? w.required_metrics : [];
          const filters = Array.isArray(w.filters) ? w.filters : [];
          const shape = w.shape_contract && w.shape_contract.kind;
          return (
            <Box key={w.widget_id || i}
              sx={{ gridColumn: { xs: 'span 12', md: `span ${Math.max(span, 4)}` }, minWidth: 0, border: '1px solid #e3eaf5', borderRadius: 2, p: 1.25, bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Chip label={i + 1} size="small" color="primary" sx={{ height: 20, minWidth: 20, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }} />
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, flex: '1 1 auto' }}>{w.title || w.widget_id}</Typography>
                {w.preferred_viz && <Chip size="small" variant="outlined" sx={CHIP_WRAP_SX} label={vizLabel(w.preferred_viz)} />}
              </Box>
              <Stack spacing={0.25}>
                {w.goal && <Row label={t('Dashboards.hitl.widgetGoal')}><Typography variant="caption">{w.goal}</Typography></Row>}
                {metrics.length > 0 && (
                  <Row label={t('Dashboards.hitl.widgetMetrics')}>
                    <Typography variant="caption">{metrics.join(', ')}{w.grain ? ` · ${w.grain}` : ''}</Typography>
                  </Row>
                )}
                {filters.length > 0 && (
                  <Row label={t('Dashboards.diff.ops.add_widget_filter')}>
                    <Typography variant="caption">
                      {filters.map((f) => f.filter_condition || f.filter_name).filter(Boolean).join(' · ')}
                    </Typography>
                  </Row>
                )}
                {w.preferred_viz && (
                  <Row label={t('Dashboards.hitl.widgetViz')}><Typography variant="caption">{vizLabel(w.preferred_viz)}</Typography></Row>
                )}
                {shape && <Row label={t('Dashboards.hitl.widgetShape')}><Typography variant="caption">{shape}</Typography></Row>}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
